import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Save, X, HelpCircle, LayoutGrid, List } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './CreditNoteSettings.css';
import { getSettingBySection, getInvoiceCurrencyList, insertUpdateSettings } from '../../../utils/lead';
import { getSession } from '../../../getSession';
import Loader from '../../../components/Loader/Loader';
import { useModulePermissions } from '../../../hooks/useModulePermissions';

const quillModules = {
    toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        ['clean']
    ]
};

const PREDEFINED_PLACEHOLDERS = [
    'ItemName',
    'ItemDescription',
    'Rate',
    'Quantity',
    'Amount',
    'SKU',
    'Unit',
    'TaxPercentage',
    'TaxAmount',
    'DiscountPercentage',
    'DiscountAmount',
    'TotalAmount'
];

const ENTITY_COLUMN_OPTIONS = [
    'Product Name',
    'Description',
    'SKU',
    'HSN/SAC',
    'Quantity',
    'Unit',
    'Price/Rate',
    'Tax Name',
    'Tax Rate',
    'Tax Amount',
    'Discount',
    'Amount'
];

const CreditNoteSettings = ({ onBack }) => {
    // Role based access control with module code PAG10231
    const { permissions, loading: permLoading, canEdit, canView, PermissionDeniedComponent } = useModulePermissions('PAG10231');
    const { userId } = getSession();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [currencies, setCurrencies] = useState([]);
    // List = stacked sections, Grid = 2-column card layout
    const [viewMode, setViewMode] = useState('list');

    // Route-aware back handler: use prop if provided (overlay mode), else navigate
    const handleBack = () => {
        if (onBack) {
            onBack();
        } else {
            navigate('/credit-notes');
        }
    };

    // ── Settings Field State ───────────────────────────────────────────────────
    const [numberPrefix, setNumberPrefix] = useState('CN-');
    const [financialYearFormat, setFinancialYearFormat] = useState('YY-YY');
    const [nextNumber, setNextNumber] = useState(1);
    const [paymentDueDays, setPaymentDueDays] = useState(0);
    const [isResetNumber, setIsResetNumber] = useState(false);

    const [title, setTitle] = useState('Credit Note');
    const [subheading, setSubheading] = useState('');
    const [sellerTag, setSellerTag] = useState('Sold By');
    const [companyCurrency, setCompanyCurrency] = useState(101);

    const [hideItemGrid, setHideItemGrid] = useState(false);
    const [hideItemDescription, setHideItemDescription] = useState(false);
    const [isSrNo, setIsSrNo] = useState(false);
    const [hideAmount, setHideAmount] = useState(false);

    const [itemNameRadio, setItemNameRadio] = useState('I'); // I, P, S, C
    const [itemNameCustom, setItemNameCustom] = useState('');
    const [incluseSKU, setIncluseSKU] = useState(false);
    const [incluseUnit, setIncluseUnit] = useState(false);

    const [itemEntityDescription, setItemEntityDescription] = useState('');
    const [itemEntity, setItemEntity] = useState([]);
    const [priceNameRadio, setPriceNameRadio] = useState('P'); // P, R, C
    const [priceNameCustom, setPriceNameCustom] = useState('');
    const [quantityNameRadio, setQuantityNameRadio] = useState('Q'); // Q, C
    const [quantityNameCustom, setQuantityNameCustom] = useState('');

    const [isAuthorizedSignatory, setIsAuthorizedSignatory] = useState(false);
    const [salesOwner, setSalesOwner] = useState(false);

    const [isCustomerNotes, setIsCustomerNotes] = useState(false);
    const [includeNotes, setIncludeNotes] = useState('');

    const [isTermsCondition, setIsTermsCondition] = useState(false);
    const [termsCondition, setTermsCondition] = useState('');

    const [isDigitalSignatory, setIsDigitalSignatory] = useState(false);
    const [digitalSignatoryNote, setDigitalSignatoryNote] = useState('');

    const [colorTheme, setColorTheme] = useState('#3b82f6');
    const [colorFont, setColorFont] = useState('#0f172a');

    // Hidden configurations supported in state/binding
    const [includeHeader, setIncludeHeader] = useState('');
    const [includeFooter, setIncludeFooter] = useState('');
    const [includeForHeader, setIncludeForHeader] = useState([]);
    const [includeForFooter, setIncludeForFooter] = useState([]);
    const [hideItemName, setHideItemName] = useState(false);
    const [hidePrice, setHidePrice] = useState(false);
    const [hideQuantity, setHideQuantity] = useState(false);
    const [letCustomerAddNote, setLetCustomerAddNote] = useState(false);
    const [allowEdition, setAllowEdition] = useState(false);

    // Local UI states
    const [showPlaceholderMenu, setShowPlaceholderMenu] = useState(false);
    const [tagInput, setTagInput] = useState('');
    const [showTagSuggestions, setShowTagSuggestions] = useState(false);

    const placeholderMenuRef = useRef(null);
    const tagInputRef = useRef(null);

    // ── Fetch & Bind settings (equivalent to BindCreditNoteSetting) ───────────
    const loadSettingsData = async () => {
        setLoading(true);
        try {
            const [settingsList, currencyList] = await Promise.all([
                getSettingBySection({ UserId: userId, Section: 'creditnote' }),
                getInvoiceCurrencyList()
            ]);

            setCurrencies(currencyList || []);

            const getVal = (key, defaultVal = '') => {
                const found = settingsList.find(s => s.Key?.toLowerCase() === key.toLowerCase());
                return found ? found.Value : defaultVal;
            };

            const getBool = (key, defaultVal = false) => {
                const val = getVal(key);
                if (val === '' || val === null) return defaultVal;
                return val === 'true' || val === true || val === '1' || val === 1;
            };

            // Populate Numbering settings
            setNumberPrefix(getVal('NumberPrefix', 'CN-'));
            setFinancialYearFormat(getVal('FinancialYearFormat', 'YY-YY'));
            setNextNumber(Number(getVal('NextNumber', '1')) || 1);
            setPaymentDueDays(Number(getVal('PaymentDueDays', '0')) || 0);
            setIsResetNumber(getBool('IsResetNumber', false));

            // Populate Title settings
            setTitle(getVal('Title', 'Credit Note'));
            setSubheading(getVal('Subheading', ''));
            setSellerTag(getVal('SellerTag', 'Sold By'));
            setCompanyCurrency(Number(getVal('CurrencyId', '101')) || 101);

            // Populate Item settings
            const isGridHidden = getBool('HideItemGrid', false);
            setHideItemGrid(isGridHidden);
            setHideItemDescription(getBool('HideItemDescription', false));
            setIsSrNo(getBool('IsSrNo', false));
            setHideAmount(getBool('HideAmount', false));

            const dbItemName = getVal('ItemName', 'Items');
            if (dbItemName === 'Items') {
                setItemNameRadio('I');
            } else if (dbItemName === 'Products') {
                setItemNameRadio('P');
            } else if (dbItemName === 'Services') {
                setItemNameRadio('S');
            } else {
                setItemNameRadio('C');
                setItemNameCustom(dbItemName);
            }

            setHideItemName(getBool('HideItemName', false));

            const dbPriceName = getVal('PriceName', 'Price');
            if (dbPriceName === 'Price') {
                setPriceNameRadio('P');
            } else if (dbPriceName === 'Rate') {
                setPriceNameRadio('R');
            } else {
                setPriceNameRadio('C');
                setPriceNameCustom(dbPriceName);
            }

            setHidePrice(getBool('HidePrice', false));

            const dbQuantityName = getVal('QuantityName', 'Quantity');
            if (dbQuantityName === 'Quantity') {
                setQuantityNameRadio('Q');
            } else {
                setQuantityNameRadio('C');
                setQuantityNameCustom(dbQuantityName);
            }

            setHideQuantity(getBool('HideQuantity', false));
            setItemEntity(getVal('ItemEntity', '').split(',').filter(Boolean));
            setItemEntityDescription(getVal('ItemEntityDescription', ''));
            setIncluseSKU(getBool('IncluseSKU', false));
            setIncluseUnit(getBool('IncluseUnit', false));

            // Populate Signatories & Owners
            setIsAuthorizedSignatory(getBool('IsAuthorizedSignatory', false));
            setSalesOwner(getBool('SalesOwner', false));
            setIsCustomerNotes(getBool('IsCustomerNotes', false));
            setIncludeNotes(getVal('IncludeNotes', ''));
            setIsTermsCondition(getBool('IsTermsCondition', false));
            setTermsCondition(getVal('TermsCondition', ''));
            setIsDigitalSignatory(getBool('IsDigitalSignatory', false));
            setDigitalSignatoryNote(getVal('DigitalSignatoryNote', ''));

            // Populate Colors
            setColorTheme(getVal('ColorTheme', '#3b82f6'));
            setColorFont(getVal('ColorFont', '#0f172a'));

            // Hidden configs
            setIncludeHeader(getVal('IncludeHeader', ''));
            setIncludeFooter(getVal('IncludeFooter', ''));
            setIncludeForHeader(getVal('IncludeForHeader', '').split(',').filter(Boolean));
            setIncludeForFooter(getVal('IncludeForFooter', '').split(',').filter(Boolean));
            setLetCustomerAddNote(getBool('LetCustomerAddNote', false));
            setAllowEdition(getBool('AllowEdition', false));

        } catch (err) {
            console.error('Error fetching settings:', err);
            Swal.fire('Error', 'Failed to load Credit Note settings.', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userId) {
            loadSettingsData();
        }
    }, [userId]);

    // Close menus on click outside
    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (placeholderMenuRef.current && !placeholderMenuRef.current.contains(e.target)) {
                setShowPlaceholderMenu(false);
            }
            if (tagInputRef.current && !tagInputRef.current.contains(e.target)) {
                setShowTagSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    // ── Placeholder drop insert handler ────────────────────────────────────────
    const handleInsertPlaceholder = (placeholder) => {
        const textToInsert = ` #${placeholder}# `;
        setItemEntityDescription(prev => prev + textToInsert);
        setShowPlaceholderMenu(false);
    };

    // ── Tag Chip Select helpers ────────────────────────────────────────────────
    const handleAddTag = (tag) => {
        const trimmed = tag.trim();
        if (trimmed && !itemEntity.includes(trimmed)) {
            setItemEntity(prev => [...prev, trimmed]);
        }
        setTagInput('');
        setShowTagSuggestions(false);
    };

    const handleRemoveTag = (indexToRemove) => {
        setItemEntity(prev => prev.filter((_, idx) => idx !== indexToRemove));
    };

    // ── Save handler (equivalent to btnSaveCreditNoteSetting click) ───────────
    const handleSave = async (e) => {
        if (e) e.preventDefault();

        // Validation
        const parsedNext = parseInt(nextNumber, 10);
        if (isNaN(parsedNext) || parsedNext <= 0) {
            Swal.fire('Validation Error', 'Only positive numeric values are allowed in Next Number field.', 'warning');
            return;
        }

        setSaving(true);
        try {
            const CreditNoteSetting = [];

            CreditNoteSetting.push({ Id: 0, Key: 'NumberPrefix', Value: numberPrefix, Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'NumberDigit', Value: '0', Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'NextNumber', Value: String(parsedNext), Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'Title', Value: title, Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'Subheading', Value: subheading, Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'ColorTheme', Value: colorTheme, Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'ColorFont', Value: colorFont, Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'SellerTag', Value: sellerTag, Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'ItemEntityDescription', Value: itemEntityDescription, Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'ItemEntity', Value: itemEntity.join(','), Section: 'creditnote' });

            let finalItemName = "";
            if (itemNameRadio === 'I') finalItemName = 'Items';
            else if (itemNameRadio === 'P') finalItemName = 'Products';
            else if (itemNameRadio === 'S') finalItemName = 'Services';
            else finalItemName = itemNameCustom;
            CreditNoteSetting.push({ Id: 0, Key: 'ItemName', Value: finalItemName, Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'HideItemName', Value: String(hideItemName), Section: 'creditnote' });

            let finalPriceName = "";
            if (priceNameRadio === 'P') finalPriceName = 'Price';
            else if (priceNameRadio === 'R') finalPriceName = 'Rate';
            else finalPriceName = priceNameCustom;
            CreditNoteSetting.push({ Id: 0, Key: 'PriceName', Value: finalPriceName, Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'HidePrice', Value: String(hidePrice), Section: 'creditnote' });

            let finalQuantityName = "";
            if (quantityNameRadio === 'Q') finalQuantityName = 'Quantity';
            else finalQuantityName = quantityNameCustom;
            CreditNoteSetting.push({ Id: 0, Key: 'QuantityName', Value: finalQuantityName, Section: 'creditnote' });

            CreditNoteSetting.push({ Id: 0, Key: 'HideQuantity', Value: String(hideQuantity), Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'HideItemDescription', Value: String(hideItemDescription), Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'HideAmount', Value: String(hideAmount), Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'PaymentDueDays', Value: String(paymentDueDays), Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'SalesOwner', Value: String(salesOwner), Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'AllowEdition', Value: String(allowEdition), Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'IncluseSKU', Value: String(incluseSKU), Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'IncluseUnit', Value: String(incluseUnit), Section: 'creditnote' });

            CreditNoteSetting.push({ Id: 0, Key: 'IncludeHeader', Value: includeHeader, Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'IncludeFooter', Value: includeFooter, Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'IncludeNotes', Value: includeNotes, Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'TermsCondition', Value: termsCondition, Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'IncludeForHeader', Value: includeForHeader.join(','), Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'IncludeForFooter', Value: includeForFooter.join(','), Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'LetCustomerAddNote', Value: String(letCustomerAddNote), Section: 'creditnote' });

            CreditNoteSetting.push({ Id: 0, Key: 'FinancialYearFormat', Value: financialYearFormat, Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'HideItemGrid', Value: String(hideItemGrid), Section: 'creditnote' });

            CreditNoteSetting.push({ Id: 0, Key: 'IsCustomerNotes', Value: String(isCustomerNotes), Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'IsTermsCondition', Value: String(isTermsCondition), Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'IsAuthorizedSignatory', Value: String(isAuthorizedSignatory), Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'IsDigitalSignatory', Value: String(isDigitalSignatory), Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'IsSrNo', Value: String(isSrNo), Section: 'creditnote' });

            CreditNoteSetting.push({ Id: 0, Key: 'DigitalSignatoryNote', Value: digitalSignatoryNote, Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'CurrencyId', Value: String(companyCurrency), Section: 'creditnote' });
            CreditNoteSetting.push({ Id: 0, Key: 'IsResetNumber', Value: String(isResetNumber), Section: 'creditnote' });

            const formattedSettings = CreditNoteSetting.map(item => ({
                Id: Number(item.Id) || 0,
                Key: String(item.Key),
                Value: String(item.Value),
                Section: String(item.Section),
                ParentId: 0
            }));

            const resp = await insertUpdateSettings({
                InvoiceSettings: formattedSettings,
                UserId: Number(userId)
            });

            const isSuccess = resp === true ||
                resp?.d === true ||
                resp?.Status === 1 ||
                resp?.d === "1" ||
                resp?.d === 1 ||
                resp === 1;

            if (isSuccess) {
                Swal.fire({
                    title: 'Success',
                    text: 'Credit Note settings saved successfully.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
                handleBack();
            } else {
                throw new Error(resp?.Message || 'Failed to save settings');
            }
        } catch (err) {
            console.error('Save settings error:', err);
            Swal.fire('Error', err?.message || 'Failed to save Credit Note settings.', 'error');
        } finally {
            setSaving(false);
        }
    };

    // ── Loader / Permission Check Render ────────────────────────────────────────
    if (permLoading || loading) {
        return (
            <div className="settings-loader-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <Loader message="Loading settings module..." />
            </div>
        );
    }

    if (!canView) {
        return <PermissionDeniedComponent />;
    }

    return (
        <div className="cn-settings-layout">
            <input type="hidden" id="hdnModuleCode" value="PAG10231" readOnly />

            {/* Header section */}
            <div className="cn-settings-header">
                <button className="settings-back-btn" onClick={handleBack}>
                    <ArrowLeft size={16} />
                    <span>Back</span>
                </button>
                <div className="header-titles">
                    <h2>Credit Note Settings</h2>
                    <p>Configure layouts, title configurations, number prefixes, custom columns, and signatories</p>
                </div>
                <div className="header-actions">
                    {/* List / Grid view toggle */}
                    <div className="view-toggle-group">
                        <button
                            type="button"
                            className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                            onClick={() => setViewMode('list')}
                            title="List View"
                        >
                            <List size={16} />
                        </button>
                        <button
                            type="button"
                            className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                            onClick={() => setViewMode('grid')}
                            title="Grid View"
                        >
                            <LayoutGrid size={16} />
                        </button>
                    </div>

                    <button
                        type="button"
                        id="btnResetCreditnoteSetting"
                        className="settings-reset-btn"
                        onClick={loadSettingsData}
                        disabled={saving || !canEdit}
                    >
                        Reset
                    </button>
                    <button
                        type="button"
                        id="btnSaveCreditNoteSetting"
                        className="settings-save-btn"
                        onClick={handleSave}
                        disabled={saving || !canEdit}
                    >
                        <Save size={16} />
                        <span>{saving ? 'Saving...' : 'Save Settings'}</span>
                    </button>
                </div>
            </div>

            {/* Main Content Layout */}
            <form onSubmit={handleSave} className="cn-settings-body">
                {/* Sections wrapper — switches between list and grid layout */}
                <div className={viewMode === 'grid' ? 'cn-sections-grid' : 'cn-sections-list'}>

                    {/* 1. Numbering section */}
                    <div className="creditmain">
                        <div className="creditMainHead">Numbering</div>
                        <div className="creditMainbody">
                            <div className="form-grid-2">
                                <div className="form-group">
                                    <label htmlFor="txtNumberPrefix">Number Prefix</label>
                                    <div className="input-group">
                                        <div className="input-group-prepend">
                                            <span style={{ fontSize: 13, fontWeight: 'bold' }}>A-Z</span>
                                        </div>
                                        <input
                                            type="text"
                                            id="txtNumberPrefix"
                                            placeholder="Enter Number Prefix"
                                            className="largeinput"
                                            value={numberPrefix}
                                            onChange={e => setNumberPrefix(e.target.value)}
                                            disabled={!canEdit}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="txtFinancialYearFormat">Financial Years Format</label>
                                    <select
                                        id="txtFinancialYearFormat"
                                        className="largeinput"
                                        value={financialYearFormat}
                                        onChange={e => setFinancialYearFormat(e.target.value)}
                                        disabled={!canEdit}
                                    >
                                        <option value="YY-YY">YY-YY</option>
                                        <option value="YYYY-YYYY">YYYY-YYYY</option>
                                        <option value="YYYY-YY">YYYY-YY</option>
                                        <option value="YYYY/YY">YYYY/YY</option>
                                        <option value="YY/YY">YY/YY</option>
                                        <option value="YYYY/YYYY">YYYY/YYYY</option>
                                        <option value="None">None</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="txtNextNumber">Next Number</label>
                                    <input
                                        type="number"
                                        id="txtNextNumber"
                                        placeholder="Enter Next Number"
                                        className="largeinput"
                                        value={nextNumber}
                                        onChange={e => setNextNumber(Number(e.target.value) || '')}
                                        disabled={!canEdit}
                                    />
                                </div>

                                <div className="form-group" style={{ justifyContent: 'center' }}>
                                    <label className="cont-checkbox">
                                        <input
                                            type="checkbox"
                                            id="txtResetNumber"
                                            checked={isResetNumber}
                                            onChange={e => setIsResetNumber(e.target.checked)}
                                            disabled={!canEdit}
                                        />
                                        <span>Reset Number After Financial year change</span>
                                    </label>
                                </div>

                                {/* Hidden Payment Due Days parameter (maintained for state consistency) */}
                                <div className="form-group hide" style={{ display: 'none' }}>
                                    <label htmlFor="txtPaymentDueDays">Payment Due Days</label>
                                    <input
                                        type="number"
                                        id="txtPaymentDueDays"
                                        value={paymentDueDays}
                                        onChange={e => setPaymentDueDays(Number(e.target.value) || 0)}
                                        disabled={!canEdit}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 2. Title Settings Section */}
                    <div className="creditmain">
                        <div className="creditMainHead">Title</div>
                        <div className="creditMainbody">
                            <div className="form-grid-2">
                                <div className="form-group">
                                    <label htmlFor="txtTitle">Title</label>
                                    <input
                                        type="text"
                                        id="txtTitle"
                                        placeholder="Enter Title"
                                        className="largeinput"
                                        value={title}
                                        onChange={e => setTitle(e.target.value)}
                                        disabled={!canEdit}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="txtSubheading">Subheading</label>
                                    <input
                                        type="text"
                                        id="txtSubheading"
                                        placeholder="Enter Subheading"
                                        className="largeinput"
                                        value={subheading}
                                        onChange={e => setSubheading(e.target.value)}
                                        disabled={!canEdit}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="txtSellerTag">Seller Tag</label>
                                    <input
                                        type="text"
                                        id="txtSellerTag"
                                        placeholder="Enter Seller Tag"
                                        className="largeinput"
                                        value={sellerTag}
                                        onChange={e => setSellerTag(e.target.value)}
                                        disabled={!canEdit}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="txtCompnayCurrency">Currency</label>
                                    <select
                                        id="txtCompnayCurrency"
                                        className="largeinput"
                                        value={companyCurrency}
                                        onChange={e => setCompanyCurrency(Number(e.target.value))}
                                        disabled={!canEdit}
                                    >
                                        <option value="">-- Select Currency --</option>
                                        {currencies.map(curr => (
                                            <option key={curr.Id || curr.ID} value={curr.Id || curr.ID}>
                                                {curr.Name} ({curr.Symbol})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 3. Item Setting Section */}
                    <div className="creditmain">
                        <div className="creditMainHead">Item Setting</div>
                        <div className="creditMainbody">

                            <div className="switch-container" style={{ marginBottom: 20 }}>
                                <div className="switch-label-desc">
                                    <label htmlFor="txtHideItemGrid">Hide Item Grid</label>
                                    <span>Enable this to view and customize item configurations below</span>
                                </div>
                                <label className="switch-flat">
                                    <input
                                        type="checkbox"
                                        id="txtHideItemGrid"
                                        checked={hideItemGrid}
                                        onChange={e => setHideItemGrid(e.target.checked)}
                                        disabled={!canEdit}
                                    />
                                    <span className="slider"></span>
                                </label>
                            </div>

                            {/* Shows details based on HideItemGrid value */}
                            {hideItemGrid && (
                                <div className="form-grid-2 fade-in-element">
                                    <div className="form-group itemElement">
                                        <label className="cont-checkbox">
                                            <input
                                                type="checkbox"
                                                id="txtHideItemDescription"
                                                checked={hideItemDescription}
                                                onChange={e => setHideItemDescription(e.target.checked)}
                                                disabled={!canEdit}
                                            />
                                            <span>Hide Item Description</span>
                                        </label>
                                    </div>

                                    <div className="form-group itemElement">
                                        <label className="cont-checkbox">
                                            <input
                                                type="checkbox"
                                                id="shwSrNo"
                                                checked={isSrNo}
                                                onChange={e => setIsSrNo(e.target.checked)}
                                                disabled={!canEdit}
                                            />
                                            <span>Include Serial No</span>
                                        </label>
                                    </div>

                                    {/* Item Name Radios */}
                                    <div className="form-group itemElement full-width">
                                        <label>Item Name</label>
                                        <div className="input-group-invoice-text">
                                            <label className="radio-cont">
                                                <input
                                                    type="radio"
                                                    name="txtItemName"
                                                    value="I"
                                                    checked={itemNameRadio === 'I'}
                                                    onChange={() => setItemNameRadio('I')}
                                                    disabled={!canEdit}
                                                />
                                                <span>Items</span>
                                            </label>
                                            <label className="radio-cont">
                                                <input
                                                    type="radio"
                                                    name="txtItemName"
                                                    value="P"
                                                    checked={itemNameRadio === 'P'}
                                                    onChange={() => setItemNameRadio('P')}
                                                    disabled={!canEdit}
                                                />
                                                <span>Products</span>
                                            </label>
                                            <label className="radio-cont">
                                                <input
                                                    type="radio"
                                                    name="txtItemName"
                                                    value="S"
                                                    checked={itemNameRadio === 'S'}
                                                    onChange={() => setItemNameRadio('S')}
                                                    disabled={!canEdit}
                                                />
                                                <span>Services</span>
                                            </label>
                                            <label className="radio-cont">
                                                <input
                                                    type="radio"
                                                    name="txtItemName"
                                                    value="C"
                                                    checked={itemNameRadio === 'C'}
                                                    onChange={() => setItemNameRadio('C')}
                                                    disabled={!canEdit}
                                                />
                                                <span>Custom</span>
                                            </label>
                                        </div>

                                        {itemNameRadio === 'C' && (
                                            <input
                                                type="text"
                                                id="txtItemNameCustom"
                                                placeholder="Enter Custom Item Name"
                                                className="largeinput fade-in-element"
                                                value={itemNameCustom}
                                                onChange={e => setItemNameCustom(e.target.value)}
                                                disabled={!canEdit}
                                                style={{ marginTop: 8 }}
                                            />
                                        )}
                                    </div>

                                    {/* Custom column selection tags (Autocomplete interface) */}
                                    <div className="form-group itemElement">
                                        <label htmlFor="tag-selector-input">Product Columns Selection</label>
                                        <div className="input-group" ref={tagInputRef}>
                                            <input
                                                type="text"
                                                id="tag-selector-input"
                                                placeholder="Type or click here to add columns"
                                                className="largeinput"
                                                value={tagInput}
                                                onChange={e => {
                                                    setTagInput(e.target.value);
                                                    setShowTagSuggestions(true);
                                                }}
                                                onFocus={() => setShowTagSuggestions(true)}
                                                disabled={!canEdit}
                                            />

                                            {showTagSuggestions && (
                                                <div className="sms_placeholder" style={{ left: 0, top: '100%', width: '100%', maxHeight: 200, overflowY: 'auto' }}>
                                                    <div className="panel-heading">
                                                        <strong>Predefined Columns</strong>
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                        {ENTITY_COLUMN_OPTIONS.filter(opt =>
                                                            opt.toLowerCase().includes(tagInput.toLowerCase()) && !itemEntity.includes(opt)
                                                        ).map(opt => (
                                                            <div
                                                                key={opt}
                                                                style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', background: '#f8fafc', borderRadius: 4 }}
                                                                onClick={() => handleAddTag(opt)}
                                                                onMouseEnter={e => e.currentTarget.style.background = '#e2e8f0'}
                                                                onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}
                                                            >
                                                                {opt}
                                                            </div>
                                                        ))}
                                                        {tagInput.trim() && !itemEntity.includes(tagInput.trim()) && (
                                                            <div
                                                                style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', color: '#3b82f6', fontWeight: 600 }}
                                                                onClick={() => handleAddTag(tagInput)}
                                                            >
                                                                Add custom column "{tagInput}"
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div className="actionLabelCont">
                                            {itemEntity.map((tag, idx) => (
                                                <span key={idx} className="actionlabel">
                                                    <span>{tag}</span>
                                                    {canEdit && (
                                                        <button type="button" className="delete-tag-btn" onClick={() => handleRemoveTag(idx)}>
                                                            <X size={13} />
                                                        </button>
                                                    )}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Include in Item column select description with placeholders */}
                                    <div className="form-group itemElement">
                                        <label htmlFor="txtItemColumnSelection">Include In Item Description</label>
                                        <div className="input-group" style={{ position: 'relative' }}>
                                            <textarea
                                                id="txtItemColumnSelection"
                                                placeholder="Type custom text or choose placeholders"
                                                className="largeinput"
                                                rows="3"
                                                value={itemEntityDescription}
                                                onChange={e => setItemEntityDescription(e.target.value)}
                                                disabled={!canEdit}
                                                style={{ paddingRight: 40 }}
                                            />

                                            <div style={{ position: 'absolute', right: 10, top: 10 }} ref={placeholderMenuRef}>
                                                <button
                                                    type="button"
                                                    id="btnplaceholder"
                                                    className="btn-setting"
                                                    onClick={() => setShowPlaceholderMenu(!showPlaceholderMenu)}
                                                    title="Choose placeholders"
                                                    disabled={!canEdit}
                                                    style={{ border: '1px solid #cbd5e1', borderRadius: 6, width: 28, height: 28 }}
                                                >
                                                    <HelpCircle size={15} style={{ color: '#64748b' }} />
                                                </button>

                                                {showPlaceholderMenu && (
                                                    <div className="sms_placeholder" style={{ right: 0, top: '100%', minWidth: 200 }}>
                                                        <div className="panel-heading">
                                                            <strong>Item Placeholders</strong>
                                                            <button type="button" style={{ border: 'none', background: 'transparent', cursor: 'pointer' }} onClick={() => setShowPlaceholderMenu(false)}>
                                                                <X size={12} />
                                                            </button>
                                                        </div>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 4, maxHeight: 200, overflowY: 'auto' }}>
                                                            {PREDEFINED_PLACEHOLDERS.map(ph => (
                                                                <button
                                                                    key={ph}
                                                                    type="button"
                                                                    style={{ padding: '6px 8px', textAlign: 'left', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}
                                                                    onClick={() => handleInsertPlaceholder(ph)}
                                                                >
                                                                    #{ph}#
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Price Name Radios */}
                                    <div className="form-group itemElement full-width">
                                        <label>Price Name</label>
                                        <div className="input-group-invoice-text">
                                            <label className="radio-cont">
                                                <input
                                                    type="radio"
                                                    name="txtPriceName"
                                                    value="P"
                                                    checked={priceNameRadio === 'P'}
                                                    onChange={() => setPriceNameRadio('P')}
                                                    disabled={!canEdit}
                                                />
                                                <span>Price</span>
                                            </label>
                                            <label className="radio-cont">
                                                <input
                                                    type="radio"
                                                    name="txtPriceName"
                                                    value="R"
                                                    checked={priceNameRadio === 'R'}
                                                    onChange={() => setPriceNameRadio('R')}
                                                    disabled={!canEdit}
                                                />
                                                <span>Rate</span>
                                            </label>
                                            <label className="radio-cont">
                                                <input
                                                    type="radio"
                                                    name="txtPriceName"
                                                    value="C"
                                                    checked={priceNameRadio === 'C'}
                                                    onChange={() => setPriceNameRadio('C')}
                                                    disabled={!canEdit}
                                                />
                                                <span>Custom</span>
                                            </label>
                                        </div>
                                        {priceNameRadio === 'C' && (
                                            <input
                                                type="text"
                                                id="txtPriceNameCustom"
                                                placeholder="Enter Custom Price Name"
                                                className="largeinput fade-in-element"
                                                value={priceNameCustom}
                                                onChange={e => setPriceNameCustom(e.target.value)}
                                                disabled={!canEdit}
                                                style={{ marginTop: 8 }}
                                            />
                                        )}
                                    </div>

                                    {/* Quantity Name Radios */}
                                    <div className="form-group itemElement full-width">
                                        <label>Quantity Name</label>
                                        <div className="input-group-invoice-text">
                                            <label className="radio-cont">
                                                <input
                                                    type="radio"
                                                    name="txtQuantityName"
                                                    value="Q"
                                                    checked={quantityNameRadio === 'Q'}
                                                    onChange={() => setQuantityNameRadio('Q')}
                                                    disabled={!canEdit}
                                                />
                                                <span>Quantity</span>
                                            </label>
                                            <label className="radio-cont">
                                                <input
                                                    type="radio"
                                                    name="txtQuantityName"
                                                    value="C"
                                                    checked={quantityNameRadio === 'C'}
                                                    onChange={() => setQuantityNameRadio('C')}
                                                    disabled={!canEdit}
                                                />
                                                <span>Custom</span>
                                            </label>
                                        </div>
                                        {quantityNameRadio === 'C' && (
                                            <input
                                                type="text"
                                                id="txtQuantityNameCustom"
                                                placeholder="Enter Custom Quantity Name"
                                                className="largeinput fade-in-element"
                                                value={quantityNameCustom}
                                                onChange={e => setQuantityNameCustom(e.target.value)}
                                                disabled={!canEdit}
                                                style={{ marginTop: 8 }}
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* 4. Other Settings Section */}
                    <div className="creditmain">
                        <div className="creditMainHead">Other Setting</div>
                        <div className="creditMainbody">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                                <div className="form-grid-2">
                                    <div className="form-group">
                                        <label className="cont-checkbox">
                                            <input
                                                type="checkbox"
                                                id="shwAuthorizedSignatory"
                                                checked={isAuthorizedSignatory}
                                                onChange={e => setIsAuthorizedSignatory(e.target.checked)}
                                                disabled={!canEdit}
                                            />
                                            <span>Include Authorised Signatory</span>
                                        </label>
                                    </div>

                                    <div className="form-group">
                                        <label className="cont-checkbox">
                                            <input
                                                type="checkbox"
                                                id="txtSalesOwner"
                                                checked={salesOwner}
                                                onChange={e => setSalesOwner(e.target.checked)}
                                                disabled={!canEdit}
                                            />
                                            <span>Add Sales Owner</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Customer Notes */}
                                <div className="form-group" style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
                                    <label className="cont-checkbox">
                                        <input
                                            type="checkbox"
                                            id="shwCustomerNotes"
                                            checked={isCustomerNotes}
                                            onChange={e => setIsCustomerNotes(e.target.checked)}
                                            disabled={!canEdit}
                                        />
                                        <span>Include Customer Notes</span>
                                    </label>

                                    {isCustomerNotes && (
                                        <div className="text-editor-wrapper fade-in-element" style={{ marginTop: 12 }}>
                                            <label htmlFor="txtIncludeNotes">Customer Notes / Greeting Template</label>
                                            <ReactQuill
                                                theme="snow"
                                                value={includeNotes}
                                                onChange={setIncludeNotes}
                                                modules={quillModules}
                                                readOnly={!canEdit}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Terms & Conditions */}
                                <div className="form-group" style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
                                    <label className="cont-checkbox">
                                        <input
                                            type="checkbox"
                                            id="shwTermsCondition"
                                            checked={isTermsCondition}
                                            onChange={e => setIsTermsCondition(e.target.checked)}
                                            disabled={!canEdit}
                                        />
                                        <span>Include Terms & Conditions</span>
                                    </label>

                                    {isTermsCondition && (
                                        <div className="text-editor-wrapper fade-in-element" style={{ marginTop: 12 }}>
                                            <label htmlFor="txtTermsCondition">Terms & Conditions Template</label>
                                            <ReactQuill
                                                theme="snow"
                                                value={termsCondition}
                                                onChange={setTermsCondition}
                                                modules={quillModules}
                                                readOnly={!canEdit}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Digital Signatory */}
                                <div className="form-group" style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
                                    <label className="cont-checkbox">
                                        <input
                                            type="checkbox"
                                            id="shwDigitalSignatory"
                                            checked={isDigitalSignatory}
                                            onChange={e => setIsDigitalSignatory(e.target.checked)}
                                            disabled={!canEdit}
                                        />
                                        <span>Include Digital Signatory</span>
                                    </label>

                                    {isDigitalSignatory && (
                                        <div className="fade-in-element" style={{ marginTop: 12 }}>
                                            <label htmlFor="txtDigitalSignaltoryNote">Digital Signatory Note</label>
                                            <input
                                                type="text"
                                                id="txtDigitalSignaltoryNote"
                                                placeholder="Enter Digital Signatory Note"
                                                className="largeinput"
                                                value={digitalSignatoryNote}
                                                onChange={e => setDigitalSignatoryNote(e.target.value)}
                                                disabled={!canEdit}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 5. Look & Feel Section */}
                    <div className="creditmain">
                        <div className="creditMainHead">Look & Feel</div>
                        <div className="creditMainbody">
                            <div className="form-grid-2">
                                <div className="form-group">
                                    <label htmlFor="txtColorTheme">Theme Color</label>
                                    <div className="color-field-group">
                                        <input
                                            type="color"
                                            id="txtColorTheme"
                                            value={colorTheme}
                                            onChange={e => setColorTheme(e.target.value)}
                                            disabled={!canEdit}
                                        />
                                        <input
                                            type="text"
                                            className="color-hex-text"
                                            value={colorTheme}
                                            onChange={e => setColorTheme(e.target.value)}
                                            disabled={!canEdit}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="txtColorFont">Font Color</label>
                                    <div className="color-field-group">
                                        <input
                                            type="color"
                                            id="txtColorFont"
                                            value={colorFont}
                                            onChange={e => setColorFont(e.target.value)}
                                            disabled={!canEdit}
                                        />
                                        <input
                                            type="text"
                                            className="color-hex-text"
                                            value={colorFont}
                                            onChange={e => setColorFont(e.target.value)}
                                            disabled={!canEdit}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>{/* end cn-sections-grid / cn-sections-list */}
            </form>
        </div>
    );
};

export default CreditNoteSettings;
